import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { transformImage } from '@/lib/imageTransform';
import { formatPrice } from '@/lib/formatPrice';
import { DisplayPrice } from '@/components/DisplayPrice';
import { useLanguage } from '@/hooks/useLanguage';

interface VariationData {
  id: string;
  label: string;
  price: number;
  quantity: number;
}

interface BumpData {
  id: string;
  product_id: string;
  variation_ids: string[];
  trigger_product_ids: string[];
  headline: string;
  description: string;
  discount_type: string;
  discount_value: number;
  display_mode: string;
  variation_descriptions: Record<string, string>;
  variation_discounts: Record<string, { type: 'percent' | 'fixed'; value: number }>;
  banner_label: string;
  product: {
    title: string;
    image_url: string | null;
    input_label: string;
    input_placeholder: string | null;
  };
  variations: VariationData[];
}

export interface SelectedBump {
  bumpId: string;
  productId: string;
  productTitle: string;
  productImage: string | null;
  variationId: string | null;
  variationLabel: string;
  originalPrice: number;
  finalPrice: number;
  quantity: number;
  target: string;
}

interface CheckoutBumpsProps {
  cartProductIds: string[];
  selectedBumps: SelectedBump[];
  onToggleBump: (bump: SelectedBump) => void;
  onUpdateBumpTarget: (bumpId: string, target: string) => void;
  onUpdateBumpVariation: (bump: SelectedBump) => void;
}

export default function CheckoutBumps({ cartProductIds, selectedBumps, onToggleBump, onUpdateBumpTarget, onUpdateBumpVariation }: CheckoutBumpsProps) {
  const [bumps, setBumps] = useState<BumpData[]>([]);
  const [chosenVariations, setChosenVariations] = useState<Record<string, string>>({});
  const [bumpTargets, setBumpTargets] = useState<Record<string, string>>({});
  const { locale } = useLanguage();

  // Compute effective discount for a specific variation, respecting per-variation overrides.
  const getEffectiveDiscount = (bump: BumpData, variationId: string) => {
    const override = (bump.variation_discounts || {})[variationId];
    return {
      type: (override?.type ?? bump.discount_type) as 'percent' | 'fixed',
      value: Number(override?.value ?? bump.discount_value) || 0,
    };
  };
  const applyDiscount = (price: number, type: 'percent' | 'fixed', value: number) =>
    type === 'percent' ? price * (1 - value / 100) : Math.max(0, price - value);

  useEffect(() => {
    const fetchBumps = async () => {
      const { data } = await supabase
        .from('checkout_bumps')
        .select('id, product_id, variation_ids, trigger_product_ids, headline, description, discount_type, discount_value, display_mode, variation_descriptions, variation_discounts, banner_label, products(title, image_url, input_label, input_placeholder)')
        .eq('is_active', true)
        .order('sort_order');

      if (data) {
        // Collect all variation IDs from all bumps
        const allVarIds = (data as any[]).flatMap(d => d.variation_ids || []);
        let variationsMap = new Map<string, VariationData>();

        if (allVarIds.length > 0) {
          const { data: vars } = await supabase
            .from('product_variations')
            .select('id, label, price, quantity')
            .in('id', allVarIds);
          if (vars) {
            for (const v of vars) {
              variationsMap.set(v.id, v as VariationData);
            }
          }
        }

        // For bumps with no variation_ids, fetch first variation of product
        const needVariation = (data as any[]).filter(b => !b.variation_ids || b.variation_ids.length === 0);
        if (needVariation.length > 0) {
          const productIds = [...new Set(needVariation.map(b => b.product_id))];
          const { data: vars } = await supabase
            .from('product_variations')
            .select('id, label, price, quantity, product_id')
            .in('product_id', productIds)
            .order('sort_order');
          if (vars) {
            const firstVarByProduct = new Map<string, any>();
            for (const v of vars) {
              if (!firstVarByProduct.has(v.product_id)) {
                firstVarByProduct.set(v.product_id, v);
              }
            }
            for (const b of needVariation) {
              const fv = firstVarByProduct.get(b.product_id);
              if (fv) {
                b.variation_ids = [fv.id];
                variationsMap.set(fv.id, { id: fv.id, label: fv.label, price: fv.price, quantity: fv.quantity });
              }
            }
          }
        }

        const mapped: BumpData[] = (data as any[]).map(d => ({
          ...d,
          product: d.products,
          variations: (d.variation_ids || [])
            .map((vid: string) => variationsMap.get(vid))
            .filter(Boolean)
            .sort((a: VariationData, b: VariationData) => a.price - b.price) as VariationData[],
        }));

        // Second pass: for bumps with stale/empty variations, fetch product's current variations
        const staleBumps = mapped.filter(b => b.variations.length === 0 && b.product_id);
        if (staleBumps.length > 0) {
          const staleProductIds = [...new Set(staleBumps.map(b => b.product_id))];
          const { data: fallbackVars } = await supabase
            .from('product_variations')
            .select('id, label, price, quantity, product_id')
            .in('product_id', staleProductIds)
            .order('sort_order');
          if (fallbackVars) {
            const varsByProduct = new Map<string, VariationData[]>();
            for (const v of fallbackVars) {
              const arr = varsByProduct.get(v.product_id) || [];
              arr.push({ id: v.id, label: v.label, price: v.price, quantity: v.quantity });
              varsByProduct.set(v.product_id, arr);
            }
            for (const b of staleBumps) {
              const pvars = varsByProduct.get(b.product_id);
              if (pvars && pvars.length > 0) {
                b.variations = pvars.sort((a, b) => a.price - b.price);
              }
            }
          }
        }

        setBumps(mapped);

        // Set default chosen variation for each bump
        const defaults: Record<string, string> = {};
        for (const b of mapped) {
          if (b.variations.length > 0) {
            defaults[b.id] = b.variations[0].id;
          }
        }
        setChosenVariations(defaults);

        // ── Locale overrides ────────────────────────────────
        // For non-German locales, layer translations from
        // `checkout_bump_translations` and `product_translations`.
        if (locale !== 'de' && mapped.length > 0) {
          const bumpIds = mapped.map(b => b.id);
          const productIds = [...new Set(mapped.map(b => b.product_id).filter(Boolean))];
          const [bumpTrRes, prodTrRes] = await Promise.all([
            supabase
              .from('checkout_bump_translations')
              .select('bump_id, headline, description, banner_label, variation_descriptions')
              .eq('locale', locale)
              .in('bump_id', bumpIds),
            productIds.length > 0
              ? supabase
                  .from('product_translations')
                  .select('product_id, title, input_label, input_placeholder')
                  .eq('locale', locale)
                  .in('product_id', productIds)
              : Promise.resolve({ data: [] as any[] }),
          ]);
          const bumpTrByBump = new Map<string, any>();
          for (const t of (bumpTrRes.data || [])) bumpTrByBump.set(t.bump_id, t);
          const prodTrByProduct = new Map<string, any>();
          for (const t of (prodTrRes.data || [])) prodTrByProduct.set(t.product_id, t);

          const translated = mapped.map(b => {
            const bt = bumpTrByBump.get(b.id);
            const pt = prodTrByProduct.get(b.product_id);
            return {
              ...b,
              headline: bt?.headline || b.headline,
              description: bt?.description || b.description,
              banner_label: bt?.banner_label || b.banner_label,
              variation_descriptions: {
                ...(b.variation_descriptions || {}),
                ...((bt?.variation_descriptions as Record<string, string>) || {}),
              },
              product: {
                ...b.product,
                title: pt?.title || b.product?.title,
                input_label: pt?.input_label || b.product?.input_label,
                input_placeholder: pt?.input_placeholder || b.product?.input_placeholder,
              },
            };
          });
          setBumps(translated);
        }
      }
    };
    fetchBumps();
  }, [locale]);

  // Filter bumps based on trigger products
  const visibleBumps = bumps.filter(bump => {
    const triggers = bump.trigger_product_ids || [];
    if (triggers.length === 0) return true;
    return triggers.some(tid => cartProductIds.includes(tid));
  });

  if (visibleBumps.length === 0) return null;

  return (
    <div className="space-y-3">
      {visibleBumps.map(bump => {
        if (bump.variations.length === 0) return null;

        const chosenVarId = chosenVariations[bump.id] || bump.variations[0].id;
        const chosenVar = bump.variations.find(v => v.id === chosenVarId) || bump.variations[0];

        const originalPrice = chosenVar.price;
        const chosenEff = getEffectiveDiscount(bump, chosenVar.id);
        const discountedPrice = applyDiscount(originalPrice, chosenEff.type, chosenEff.value);
        const isSelected = selectedBumps.some(sb => sb.bumpId === bump.id);
        const currentTarget = bumpTargets[bump.id] || '';

        const bumpItem: SelectedBump = {
          bumpId: bump.id,
          productId: bump.product_id,
          productTitle: bump.product.title,
          productImage: bump.product.image_url,
          variationId: chosenVarId,
          variationLabel: chosenVar.label,
          originalPrice,
          finalPrice: Math.round(discountedPrice * 100) / 100,
          quantity: chosenVar.quantity,
          target: currentTarget,
        };

        const handleVariationChange = (varId: string) => {
          setChosenVariations(prev => ({ ...prev, [bump.id]: varId }));
          if (isSelected) {
            const newVar = bump.variations.find(v => v.id === varId);
            if (newVar) {
              const newOriginal = newVar.price;
              const eff = getEffectiveDiscount(bump, newVar.id);
              const newDiscounted = applyDiscount(newOriginal, eff.type, eff.value);
              onUpdateBumpVariation({
                bumpId: bump.id,
                productId: bump.product_id,
                productTitle: bump.product.title,
                productImage: bump.product.image_url,
                variationId: varId,
                variationLabel: newVar.label,
                originalPrice: newOriginal,
                finalPrice: Math.round(newDiscounted * 100) / 100,
                quantity: newVar.quantity,
                target: currentTarget,
              });
            }
          }
        };

        const handleTargetChange = (value: string) => {
          setBumpTargets(prev => ({ ...prev, [bump.id]: value }));
          if (isSelected) {
            onUpdateBumpTarget(bump.id, value);
          }
        };

        return (
          <div key={bump.id}>
            {bump.banner_label && (
              <div className="mb-0 inline-block rounded-t-lg bg-destructive px-3 py-1 text-xs font-bold uppercase tracking-wide text-destructive-foreground">
                {bump.banner_label}
              </div>
            )}
            <div
              className={`rounded-xl border-2 border-dashed p-4 transition-colors cursor-pointer ${
                bump.banner_label ? 'rounded-tl-none' : ''
              } ${
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40'
              }`}
              onClick={() => onToggleBump({ ...bumpItem, target: currentTarget })}
            >
            <div className="flex items-start gap-3">
              {bump.product.image_url && (
                <img
                  src={transformImage(bump.product.image_url, { width: 56, height: 56, resize: 'contain' })}
                  alt={bump.product.title}
                  className="h-14 w-14 shrink-0 rounded-lg object-contain"
                  loading="lazy"
                  width={56}
                  height={56}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] border border-black bg-white shadow-sm">
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleBump({ ...bumpItem, target: currentTarget })}
                      onClick={e => e.stopPropagation()}
                      className="h-5 w-5 rounded-[2px] border-0 bg-transparent shadow-none data-[state=checked]:bg-black data-[state=checked]:text-white"
                    />
                  </span>
                  <span className="text-sm font-semibold inline-flex items-center gap-1"><span className="text-destructive text-3xl font-black leading-none -translate-y-0.5" style={{ WebkitTextStroke: '1px currentColor' }}>←</span> {bump.headline}</span>
                </div>
                <p className="text-sm font-bold">{bump.product.title}</p>
                {bump.description && (
                  <p className="mt-0.5 text-xs text-muted-foreground">{bump.description}</p>
                )}
              </div>
            </div>

            {/* Varianten, Beschreibung & Input – volle Breite unterhalb */}
            <div className="mt-3">
              {bump.variations.length > 1 && bump.display_mode === 'cards' ? (
                  <>
                    <div className="flex flex-wrap gap-1.5">
                      {bump.variations.map(v => {
                        const eff = getEffectiveDiscount(bump, v.id);
                        const vDiscount = applyDiscount(v.price, eff.type, eff.value);
                        const isChosen = v.id === chosenVarId;
                        return (
                          <button
                            key={v.id}
                            type="button"
                            onClick={e => { e.stopPropagation(); handleVariationChange(v.id); }}
                            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                              isChosen
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border bg-card text-foreground hover:border-primary/40'
                            }`}
                          >
                            {v.label} – <span className="line-through text-muted-foreground"><DisplayPrice chf={v.price} withSpace variationId={v.id} /></span>{' '}
                            <span className="font-bold"><DisplayPrice chf={vDiscount} withSpace variationId={v.id} /></span>{' '}
                            {eff.type === 'percent' && eff.value > 0 && (
                              <span className="text-destructive font-bold">-{eff.value}%</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {(() => {
                      const vDesc = (bump.variation_descriptions || {})[chosenVarId];
                      return vDesc ? (
                        <p className="mt-2 text-xs text-muted-foreground">{vDesc}</p>
                      ) : null;
                    })()}
                  </>
                ) : bump.variations.length > 1 ? (
                  <div>
                    <select
                      value={chosenVarId}
                      onChange={e => { e.stopPropagation(); handleVariationChange(e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      className="w-full rounded-lg border bg-card px-2 py-1.5 text-xs"
                    >
                      {bump.variations.map(v => {
                        const eff = getEffectiveDiscount(bump, v.id);
                        const vDiscount = applyDiscount(v.price, eff.type, eff.value);
                        return (
                          <option key={v.id} value={v.id}>
                            {v.label} – {formatPrice(v.price, undefined, true)} → {formatPrice(vDiscount, undefined, true)}{eff.type === 'percent' ? ` (-${eff.value}%)` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {(() => {
                      const vDesc = (bump.variation_descriptions || {})[chosenVarId];
                      return vDesc ? (
                        <p className="mt-2 text-xs text-muted-foreground">{vDesc}</p>
                      ) : null;
                    })()}
                  </div>
                ) : (
                  /* Single variation – show price inline */
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground line-through"><DisplayPrice chf={originalPrice} withSpace variationId={chosenVarId} /></span>
                    <span className="text-sm font-bold text-primary"><DisplayPrice chf={discountedPrice} withSpace variationId={chosenVarId} /></span>
                    {chosenEff.type === 'percent' && chosenEff.value > 0 && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold text-primary">-{chosenEff.value}%</span>
                    )}
                  </div>
                )}

                {isSelected && (
                  <div className="mt-3">
                    <Input
                      value={currentTarget}
                      onChange={e => { e.stopPropagation(); handleTargetChange(e.target.value); }}
                      onClick={e => e.stopPropagation()}
                      placeholder={bump.product.input_placeholder || 'z.B. @username'}
                      className="h-9 text-sm"
                    />
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {bump.product.input_label || 'Link / Username'}
                    </p>
                  </div>
                )}
            </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
